import React from "react";
import ScrollReporter from "./ScrollReporter";
import PropTypes from "prop-types";
import Slider from "./Slider";
import styled from "styled-components";
import { clamp } from "../../../utils/number-utils";
import { t } from "../../core/translate";

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  transform: translateZ(0);
  & > * {
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

const SliderContainer = styled.div`
  // TFA wants slider tabbable-to before the content
  order: 2;
  width: 100%;
  z-index: 1;
  position: relative;
`;

const ContentRow = styled.div`
  overflow: hidden;
  position: relative;
  transform: translateZ(0);
  order: 1;
`;

const InnerContainer = styled.ul`
  display: flex;
  width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  list-style-type: none;
  margin: 0;
  padding: 0;
  margin-bottom: -30px;
  padding-bottom: 30px;
  position: relative;
  transform: scale(1) translateZ(0);

  & > li {
    flex-shrink: 0;
    flex-grow: 0;
  }

  // Don't cut off right margin of last item
  &:after {
    content: "";
    flex: 0 0 1px;
    margin-left: -1px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  transform: translateY(-50%);
  z-index: 1;
  opacity: 1;
  transition: opacity 0.2s;
  display: inline-block;
  ${props => !props.isShowing && "opacity: 0;"}
`;

export default class HorizontalScroller extends React.Component {
  state = {
    locationPerc: 0,
    sizeFix: 0,
    hovered: false,
    focused: false,
    canScroll: true
  };

  boundScrollBack = this.scrollBack.bind(this);
  boundScrollForward = this.scrollForward.bind(this);
  boundSetHovered = () => this.setHovered(true);
  boundSetUnHovered = () => this.setHovered(false);
  boundSetFocused = () => this.setFocused(true);
  boundSetUnFocused = () => this.setFocused(false);
  boundSliderLocChange = this.sliderLocationChanged.bind(this);
  boundScrollInfoChange = this.scrollInfoChange.bind(this);

  componentDidMount() {
    this.frameId = requestAnimationFrame(() => {
      const node = this.panel.getContainerElement();
      const delta = node.offsetHeight - node.clientHeight;
      this.setState({
        sizeFix: delta
      });
    });
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.frameId);
  }

  setFocused(focused) {
    this.setState({
      focused
    });
  }

  setHovered(hovered) {
    this.setState({
      hovered
    });
  }

  sliderLocationChanged(perc, animate) {
    const fixedPerc = clamp(perc, 0, 1);
    this.updateLocation(fixedPerc);
    this.panel.setLocation(fixedPerc, animate);
  }

  updateLocation(perc) {
    this.setState({
      locationPerc: perc
    });
    if (this.props.onScroll) {
      this.props.onScroll(perc);
    }
  }

  scrollInfoChange(data) {
    this.updateLocation(data.perc);

    this.setState({
      canScroll: data.canScroll,
      totalWidth: data.totalWidth,
      visibleWidth: data.visibleWidth
    });

    if (this.props.onInfoChange) {
      this.props.onInfoChange(data);
    }
  }

  getVisiblePerc() {
    return (
      this.state.visibleWidth /
      (this.state.totalWidth - this.state.visibleWidth)
    );
  }

  scrollBack() {
    this.sliderLocationChanged(
      this.state.locationPerc - this.getVisiblePerc(),
      true
    );
  }

  scrollForward() {
    this.sliderLocationChanged(
      this.state.locationPerc + this.getVisiblePerc(),
      true
    );
  }

  arrowsVisible() {
    return this.state.hovered || this.state.focused;
  }

  getContainerStyle() {
    if (this.state.canScroll) {
      return null;
    }
    return {
      paddingBottom: this.props.trackPaddingVert
    };
  }

  getScrollBtnTop() {
    const offset = (30 - this.state.sizeFix) / 2;
    return `calc(50% - ${offset}px)`;
  }

  render() {
    return (
      <OuterContainer
        ref={el => (this.container = el)}
        style={this.getContainerStyle()}
        onMouseOver={this.boundSetHovered}
        onMouseOut={this.boundSetUnHovered}
        onFocus={this.boundSetFocused}
        onBlur={this.boundSetUnFocused}
      >
        {!Fraction.Helpers.isTouchDevice() &&
          this.state.canScroll &&
          !this.props.hideSlider && (
            <SliderContainer>
              <Slider
                percentage={this.state.locationPerc}
                onChange={this.boundSliderLocChange}
                trackPaddingHoriz={this.props.trackPaddingHoriz}
                trackPaddingVert={this.props.trackPaddingVert}
                thumbWidth={this.props.thumbWidth}
                thumbHeight={this.props.thumbHeight}
                trackHeight={this.props.trackHeight}
                isHighlighted={this.arrowsVisible()}
                thumbColor={this.props.thumbColor}
                label={t("slider.scrollLoc")}
              />
            </SliderContainer>
          )}
        <ContentRow style={{ marginBottom: `-${this.state.sizeFix}px` }}>
          {!Fraction.Helpers.isTouchDevice() &&
            this.state.canScroll && [
              <ButtonContainer
                key="back"
                style={{
                  left: this.props.buttonInset,
                  top: this.getScrollBtnTop()
                }}
                isShowing={this.arrowsVisible()}
              >
                <this.props.back
                  onClick={this.boundScrollBack}
                  disabled={this.state.locationPerc === 0}
                  aria-label={t("slider.scrollBack")}
                />
              </ButtonContainer>,
              <ButtonContainer
                key="forward"
                style={{
                  right: this.props.buttonInset,
                  top: this.getScrollBtnTop()
                }}
                isShowing={this.arrowsVisible()}
              >
                <this.props.forward
                  disabled={this.state.locationPerc === 1}
                  onClick={this.boundScrollForward}
                  aria-label={t("slider.scrollForward")}
                />
              </ButtonContainer>
            ]}
          <ScrollReporter
            styledContainer={InnerContainer}
            render={this.props.render}
            scrollThrottle={150}
            resizeThrottle={200}
            onChange={this.boundScrollInfoChange}
            ref={el => (this.panel = el)}
            forceUpdate={this.props.update}
          />
        </ContentRow>
      </OuterContainer>
    );
  }
}

HorizontalScroller.defaultProps = {
  buttonInset: 50,
  trackPaddingVert: 28,
  trackPaddingHoriz: 50,
  trackHeight: 2,
  thumbHeight: 8,
  thumbWidth: 44
};

HorizontalScroller.propTypes = {
  render: PropTypes.func.isRequired
};
